import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '../services/analytics.service';
import { AnalyticsContextService } from '../services/analytics-context.service';

/**
 * Global error handler that catches unhandled errors and logs them to analytics
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    private router = inject(Router);
    private analytics = inject(AnalyticsService);
    private context = inject(AnalyticsContextService);

    /**
     * Last report time per error fingerprint.
     *
     * Throttling on a single timestamp would silence every error that follows
     * within the window, which loses exactly the case worth seeing: a burst of
     * distinct failures. Keyed by fingerprint, a repeating error is suppressed
     * while a different one still reports.
     */
    private lastSeen = new Map<string, number>();
    private readonly ERROR_THROTTLE_MS = 5000;

    /** Stop the map growing without bound on a long-lived session. */
    private readonly MAX_TRACKED_FINGERPRINTS = 200;

    async handleError(error: Error): Promise<void> {
        // Get current route first - it is part of the fingerprint, since the
        // same error from two different screens is worth seeing twice.
        const currentRoute = this.router.url;

        // Extract error details
        const errorMessage = error?.message || String(error);
        const errorStack = error?.stack || 'No stack trace';
        const errorName = error?.name || 'Error';

        const fingerprint = this.analytics.fingerprint(
            errorName,
            errorMessage.split('\n')[0],
            currentRoute
        );

        const now = Date.now();
        const previous = this.lastSeen.get(fingerprint);
        if (previous !== undefined && now - previous < this.ERROR_THROTTLE_MS) {
            console.error('Error throttled (repeat within 5s):', error);
            return;
        }
        this.rememberFingerprint(fingerprint, now);

        const context = await this.context.get();

        // Log to console for debugging
        console.error('Global Error Handler:', {
            message: errorMessage,
            name: errorName,
            stack: errorStack,
            user: context.user_id,
            route: currentRoute,
            fingerprint
        });

        // Log to analytics with context.
        //
        // GA4 clips every parameter at 100 characters, so a stack trace cannot
        // survive the trip. `error_fingerprint` is what makes occurrences
        // groupable in the console; `error_location` carries the single most
        // useful stack frame. The full trace stays in the browser console, and
        // in Sentry once that is wired up.
        this.analytics.logEvent('error_monitoring', {
            action: 'uncaught_error',
            error_name: errorName,
            error_message: errorMessage,
            error_fingerprint: fingerprint,
            error_location: this.topFrame(errorStack),
            current_route: currentRoute,
            ...context
        });

        // Re-throw to ensure Angular's default error handling still works
        throw error;
    }

    /**
     * Record a fingerprint, evicting the oldest entries once the map is full.
     */
    private rememberFingerprint(fingerprint: string, now: number): void {
        if (this.lastSeen.size >= this.MAX_TRACKED_FINGERPRINTS) {
            for (const [key, seenAt] of this.lastSeen) {
                if (now - seenAt > this.ERROR_THROTTLE_MS) {
                    this.lastSeen.delete(key);
                }
            }
            // Still full - drop the oldest insertion (Map preserves insert order).
            if (this.lastSeen.size >= this.MAX_TRACKED_FINGERPRINTS) {
                const oldest = this.lastSeen.keys().next();
                if (!oldest.done) {
                    this.lastSeen.delete(oldest.value);
                }
            }
        }
        this.lastSeen.set(fingerprint, now);
    }

    /**
     * First stack frame that belongs to application code, as a compact
     * "file:line" that fits inside GA4's parameter limit.
     */
    private topFrame(stack: string): string {
        const frames = stack.split('\n').slice(1);
        for (const frame of frames) {
            const match = frame.match(/([\w.-]+\.[jt]s):(\d+):(\d+)/);
            if (match && !match[1].startsWith('zone')) {
                return `${match[1]}:${match[2]}`;
            }
        }
        return 'unknown';
    }
}
